import {
  isBlockingPendingQuestionnaire,
  isPendingQuestionnaireInOrganization,
  OrganizationUserType,
} from '@mfw/common';
import React from 'react';

import { useOrganizationContext } from '../../context/OrganizationProvider/OrganizationProvider';
import { usePendingQuestionnaireSubmissions } from './usePendingQuestionnaireSubmissions';
import { useAuthContext } from '../../context';

/**
 * Splits the account's outstanding questionnaires by the relationship they came
 * from, because the consequences differ:
 *
 * - `blockingHere` locks the app. Scoped to the organization in context — see
 *   `isBlockingPendingQuestionnaireInOrganization` for why another
 *   organization must not be able to do that.
 * - `answerableHere` is offered in a dismissible modal instead: a scheduled
 *   occurrence that has come due but carries no deadline.
 * - `waitingElsewhere` is what the account owes OTHER organizations. Surfaced,
 *   never enforced — without it a trainer who is also somebody's client would
 *   simply never learn a form was waiting.
 * - `blockingAnywhere` is what keeps the wizard open once the client is in it.
 *   Entering is scoped; finishing is not, so a client who walked in can clear
 *   everything they owe in one pass.
 *
 * Lives outside `AuthProvider` on purpose: the selected organization comes
 * from `OrganizationProvider`, which is mounted BELOW it, so the scoping
 * cannot be done there.
 */
export function useScopedPendingQuestionnaires() {
  const { currentOrganization, isLoading: isOrganizationLoading } =
    useOrganizationContext();
  const { currentUser } = useAuthContext();

  // "Is this account a client somewhere" — deliberately not `currentUser.type`,
  // which is only the role in the selected organization. Somebody who is a
  // trainer in A and a client in B must still have their B questionnaires
  // fetched; what changes per organization is what happens with them.
  const isClientSomewhere = Boolean(
    currentUser &&
    currentUser.status === 'ACTIVE' &&
    currentUser.organizationUsers?.some(
      (organizationUser) =>
        organizationUser.type === OrganizationUserType.CLIENT &&
        organizationUser.status === 'ACTIVE',
    ),
  );

  const { data: submissions, isLoading: isSubmissionsLoading } =
    usePendingQuestionnaireSubmissions({
      enabled: isClientSomewhere,
    });

  // No selected organization is a real context — the personal profile — and
  // it is indistinguishable from "the switcher has not resolved yet" by the id
  // alone. Answering before it resolves would briefly file the client's OWN
  // organization under `waitingElsewhere` and flash the dismissible modal at
  // them instead of locking the app, so hold everything back until then.
  const organizationId = currentOrganization?.id ?? null;

  return React.useMemo(() => {
    const pending = isOrganizationLoading ? [] : (submissions ?? []);
    const here = pending.filter((submission) =>
      isPendingQuestionnaireInOrganization(submission, organizationId),
    );

    return {
      answerableHere: here.find(
        (submission) => !isBlockingPendingQuestionnaire(submission),
      ),
      blockingAnywhere: pending.some(isBlockingPendingQuestionnaire),
      blockingHere: here.some(isBlockingPendingQuestionnaire),
      // False while either input is still unknown. Consumers that would act
      // on the ABSENCE of a blocking questionnaire (the wizard-exit redirect)
      // must wait for this — on a cold load `pending` is empty simply because
      // nothing has been fetched yet, which is indistinguishable from
      // "nothing owed" by the flags alone.
      isResolved:
        !isOrganizationLoading && (!isClientSomewhere || !isSubmissionsLoading),
      waitingElsewhere: pending.filter(
        (submission) =>
          !isPendingQuestionnaireInOrganization(submission, organizationId) &&
          isBlockingPendingQuestionnaire(submission),
      ),
    };
  }, [
    submissions,
    organizationId,
    isOrganizationLoading,
    isClientSomewhere,
    isSubmissionsLoading,
  ]);
}
